/**
 * Quote options (MB decision 171, Q150): one quote revision may carry several options side by side, for example
 * different block sizes or another aircraft type, each with its own areas, charges and total (TJR allowed up to 10).
 * The client chooses one; the contract is made from the chosen option only.
 *
 * Every function here works on a `QuoteAggregate` whose areas and charges carry `option_id`. A quote saved before
 * options existed (an older snapshot without `options`) is read as one option holding everything.
 */
import type { Uuid, ValidationIssue } from './model.js';
import type { QuoteAggregate, QuoteArea, QuoteCharge, QuoteOption } from './quote-model.js';
import { CHARGE_KINDS } from './quote-model.js';
import { quoteTotals, type QuoteTotals } from './quote-calc.js';
import type { TenantProfile } from './quote-model.js';

/** TJR's limit. */
export const MAX_QUOTE_OPTIONS = 10;

const LABELS = 'ABCDEFGHIJ';

/** The one option of a quote made before options existed. */
function implicitOption(agg: Pick<QuoteAggregate, 'quote'>): QuoteOption {
  return {
    id: '',
    quote_id: agg.quote.id,
    sort: 1,
    label: 'A',
    description: '',
    aircraft_type: '',
    aircraft_cruise_kt: null,
    stated_total_line_km: null,
  };
}

/** The options of a quote in their order; never empty. */
export function optionsOf(agg: Pick<QuoteAggregate, 'quote' | 'options'>): readonly QuoteOption[] {
  const list = (agg.options as readonly QuoteOption[] | undefined) ?? [];
  if (list.length === 0) return [implicitOption(agg)];
  return [...list].sort((a, b) => a.sort - b.sort);
}

/** True when the quote offers the client a choice. */
export function hasOptions(agg: Pick<QuoteAggregate, 'quote' | 'options'>): boolean {
  return optionsOf(agg).length > 1;
}

/** "Option A", or "Option A (100 m line spacing)": how checks name an option. */
export function optionName(o: Pick<QuoteOption, 'label' | 'description'>): string {
  const d = o.description.trim();
  return d ? `Option ${o.label} (${d})` : `Option ${o.label}`;
}

/** "Option A", or "Option A: 100 m line spacing" with its description (the printed heading). */
export function optionTitle(o: Pick<QuoteOption, 'label' | 'description'>): string {
  const d = o.description.trim();
  return d ? `Option ${o.label}: ${d}` : `Option ${o.label}`;
}

const inOption = (optionId: Uuid, single: boolean) => (row: { readonly option_id?: Uuid | undefined }) =>
  single || row.option_id === optionId || (optionId === '' && !row.option_id);

/** The areas of one option. */
export function optionAreas(agg: QuoteAggregate, optionId: Uuid): QuoteArea[] {
  const single = optionsOf(agg).length === 1;
  return agg.areas.filter(inOption(optionId, single));
}

/** The charges of one option (one row per kind). */
export function optionCharges(agg: QuoteAggregate, optionId: Uuid): QuoteCharge[] {
  const single = optionsOf(agg).length === 1;
  return agg.charges.filter(inOption(optionId, single));
}

/**
 * The quote as the client would sign it with one option chosen: that option's areas and charges, its aircraft
 * (when it names one) and only that option. Documents, totals, contracts and projects are made from this.
 */
export function optionAggregate(agg: QuoteAggregate, optionId: Uuid): QuoteAggregate {
  const option = optionsOf(agg).find((o) => o.id === optionId);
  if (!option) throw new Error(`The quote has no option ${optionId}.`);
  return {
    ...agg,
    quote: {
      ...agg.quote,
      aircraft_type: option.aircraft_type.trim() || agg.quote.aircraft_type,
      aircraft_cruise_kt: option.aircraft_cruise_kt ?? agg.quote.aircraft_cruise_kt,
    },
    options: [option],
    areas: optionAreas(agg, optionId),
    charges: optionCharges(agg, optionId),
  };
}

export interface OptionTotals {
  readonly option: QuoteOption;
  readonly agg: QuoteAggregate;
  readonly totals: QuoteTotals;
}

/** Each option with its own totals (decision 171: each option has its own total). */
export function optionTotals(
  agg: QuoteAggregate,
  tax: Pick<TenantProfile, 'tax_line_enabled' | 'tax_rate_pct'> | null,
): OptionTotals[] {
  return optionsOf(agg).map((option) => {
    const oa = optionAggregate(agg, option.id);
    return { option, agg: oa, totals: quoteTotals(oa, tax) };
  });
}

/** The next free label, A to J; null when the quote already has ten options. */
export function nextOptionLabel(options: readonly Pick<QuoteOption, 'label'>[]): string | null {
  if (options.length >= MAX_QUOTE_OPTIONS) return null;
  for (const l of LABELS) if (!options.some((o) => o.label === l)) return l;
  return null;
}

/** Empty charges of an option, one per kind. */
export function emptyOptionCharges(quoteId: Uuid, optionId: Uuid): QuoteCharge[] {
  return CHARGE_KINDS.map((kind) => ({
    quote_id: quoteId,
    option_id: optionId,
    kind,
    amount: null,
    cap_days: null,
    note: '',
    fuel_mode: 'none' as const,
  }));
}

/**
 * A new option, copied from another (its areas and charges with new ids) so only what differs is changed, e.g. the
 * spacing or the aircraft. Null when the quote already has ten options.
 */
export function addOption(
  agg: QuoteAggregate,
  copyFrom: Uuid | null,
  newId: () => Uuid,
): { readonly agg: QuoteAggregate; readonly option: QuoteOption } | null {
  const options = optionsOf(agg);
  const label = nextOptionLabel(options);
  if (label === null) return null;
  // a quote read without option rows gets its implicit option made real first
  const realOptions = options.map((o) => (o.id === '' ? { ...o, id: newId() } : o));
  const firstId = realOptions[0]!.id;
  const tag = <T extends { readonly option_id?: Uuid | undefined }>(r: T): T =>
    r.option_id ? r : { ...r, option_id: firstId };
  const base = { ...agg, options: realOptions, areas: agg.areas.map(tag), charges: agg.charges.map(tag) };
  const src = realOptions.find((o) => o.id === copyFrom) ?? null;
  const option: QuoteOption = {
    id: newId(),
    quote_id: agg.quote.id,
    sort: Math.max(...realOptions.map((o) => o.sort)) + 1,
    label,
    description: '',
    aircraft_type: src?.aircraft_type ?? '',
    aircraft_cruise_kt: src?.aircraft_cruise_kt ?? null,
    stated_total_line_km: null,
  };
  const areas = src
    ? optionAreas(base, src.id).map((a) => ({ ...a, id: newId(), option_id: option.id }))
    : [];
  const charges = src
    ? optionCharges(base, src.id).map((c) => ({ ...c, option_id: option.id }))
    : emptyOptionCharges(agg.quote.id, option.id);
  return {
    option,
    agg: {
      ...base,
      options: [...realOptions, option],
      areas: [...base.areas, ...areas],
      charges: [...base.charges, ...charges],
    },
  };
}

/** Remove an option with its areas and charges; the last option stays. */
export function removeOption(agg: QuoteAggregate, optionId: Uuid): QuoteAggregate {
  const options = optionsOf(agg);
  if (options.length <= 1) return agg;
  return {
    ...agg,
    options: options.filter((o) => o.id !== optionId),
    areas: agg.areas.filter((a) => a.option_id !== optionId),
    charges: agg.charges.filter((c) => c.option_id !== optionId),
  };
}

/** Checks of the options themselves (labels, count, areas), shown in the Areas step. */
export function validateOptions(agg: QuoteAggregate): ValidationIssue[] {
  const options = optionsOf(agg);
  const issues: ValidationIssue[] = [];
  if (options.length > MAX_QUOTE_OPTIONS) {
    issues.push({ field: 'options', message: `A quote holds up to ${MAX_QUOTE_OPTIONS} options.` });
  }
  const seen = new Set<string>();
  for (const o of options) {
    if (!o.label.trim()) issues.push({ field: `option.${o.id}.label`, message: 'Give the option a label.' });
    else if (seen.has(o.label.trim())) {
      issues.push({ field: `option.${o.id}.label`, message: `Two options are labelled ${o.label}.` });
    }
    seen.add(o.label.trim());
    if (options.length > 1 && optionAreas(agg, o.id).length === 0) {
      issues.push({
        field: `option.${o.id}.areas`,
        message: `${optionName(o)}: add at least one survey area.`,
      });
    }
  }
  return issues;
}

/**
 * The option a revision is accepted with (decision 171: the client chooses one). One option: that one. Several: the
 * chosen id must be one of them.
 */
export function acceptedOptionId(snapshot: QuoteAggregate, chosen: Uuid | null): Uuid {
  const options = optionsOf(snapshot);
  if (options.length === 1) return options[0]!.id;
  if (chosen === null || !options.some((o) => o.id === chosen)) {
    throw new Error('Choose the option the client accepted.');
  }
  return chosen;
}
